"use client";

import React, { useCallback, useEffect, useState } from "react";
import AnalytycsContext from "./context";
import { ANALITYCS_MESSAGE, AnalyticsPageType, SendAnalyticsEvent, sendAnalyticsEvent } from "@/utils/analitics";
import { GoogleAnalyticsConfig, PageData, PageType } from "@/types/site";

type Props = React.PropsWithChildren<{
  page: PageData | null;
  config?: GoogleAnalyticsConfig;
}>;

function getAnalyticsPageType(page: PageData): AnalyticsPageType {
  if (page.pageType === PageType.SRP) {
    return AnalyticsPageType.itemlist;
  }
  if (page.pageType === PageType.VDP) {
    return AnalyticsPageType.item;
  }
  if (page.pagePath === "/") {
    return AnalyticsPageType.home;
  }

  if (page.pagePath.includes("service")) {
    return AnalyticsPageType.service;
  }

  if (page.pagePath.includes("financ")) {
    return AnalyticsPageType.finance;
  }

  if (page.pagePath.includes("credit")) {
    return AnalyticsPageType.credit;
  }

  if (page.pagePath.includes("contact")) {
    return AnalyticsPageType.contact;
  }

  if (page.pagePath.includes("trade")) {
    return AnalyticsPageType.trade;
  }

  if (page.pagePath.includes("parts")) {
    return AnalyticsPageType.parts;
  }

  return AnalyticsPageType.custom;
}

const AnalytycsProvider: React.FC<Props> = ({ children, page, config }) => {
  const [isSet, setIsSet] = useState(false);
  const pageType = page ? getAnalyticsPageType(page) : AnalyticsPageType.custom;
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
  }, []);

  const sendAnalyticsEventWithPageType = useCallback<SendAnalyticsEvent>(
    (event, eventData) => {
      sendAnalyticsEvent(event, { ...eventData, page_type: pageType });
    },
    [pageType],
  );

  // Analytics event from iframe should be modified
  // page_type needs to be taken from the parent window
  const handleWindowMessage = useCallback(
    (e: MessageEvent<{ type: string; data: { event: string; eventData: {} } }>) => {
      const data = e.data;
      if (data.type === ANALITYCS_MESSAGE) {
        const { event, eventData } = data.data;

        sendAnalyticsEventWithPageType(event, eventData);
      }
    },
    [sendAnalyticsEventWithPageType],
  );
  useEffect(() => {
    window.addEventListener("message", handleWindowMessage);

    return () => {
      window.removeEventListener("message", handleWindowMessage);
    };
  }, [handleWindowMessage]);

  useEffect(() => {
    setIsSet(true);
    if (!config) {
      return;
    }

    window.gtag?.("config", config.measurementId, {
      // Use it to debug analytics in real time
      // debug_mode: true,
      // add page_type to predefined GA events like `page_view`
      page_type: pageType,
    });
  }, [config, pageType]);
  if (!isSet) {
    return null;
  }
  return (
    <AnalytycsContext.Provider value={{ pageType, sendAnalyticsEvent: sendAnalyticsEventWithPageType }}>
      {children}
    </AnalytycsContext.Provider>
  );
};

export default AnalytycsProvider;
